import type { ColliderHandle, RoomCapabilities, RoomEnvironment, RoomModule, RoomModuleDefinition } from '@/game/roomModule';
import { getZone } from '@/render/palette';
import {
  YARD_RAIN, YARD_SPAWN, YARD_ZONE_ID, createLoadingYard, loadingYardAmbience, loadingYardLighting, loadingYardSolids,
} from '@/world/loadingYard';

/**
 * Beat 6's loading yard — the first room with no roof — as a room module.
 * Until session 81 it was the last exterior branch in `main.ts`.
 *
 * **The room is static.** The storm, Kestner, the bin carry and the flash are
 * beat 6's (`YardDirector`, still wired in `main.ts`); what is left here is
 * shell, dressing, light, sky and rain. The flash's third reader, the rig's
 * sky term (D-107), is the host's: it holds every module room's hemisphere
 * light and base intensity, and only beat 6 drives it.
 *
 * `?dress=0` is not honoured, as the legacy branch never honoured it: the
 * census's empty-versus-dressed delta runs in the shed and the hedgerow, and
 * the yard's dressing is not a separable layer of `createLoadingYard`.
 */

/**
 * NOT the rig's flat background. An exterior's sky is the one thing a flat
 * colour cannot be, and the horizon has to be the zone's own fog value or the
 * far side of the yard grows a seam where the geometry stops and the sky
 * starts (D-106). It rains for the whole beat, so the density is a constant
 * rather than a cue; `advanceRain` on the render step is what moves it.
 */
function yardEnvironment(): RoomEnvironment {
  const zone = getZone(YARD_ZONE_ID);
  if (zone === undefined) throw new Error(`unknown zone "${YARD_ZONE_ID}"`);
  return {
    zone,
    lighting: loadingYardLighting(),
    background: { kind: 'sky', seed: 6 },
    spawn: YARD_SPAWN,
    ambience: loadingYardAmbience(),
    rain: YARD_RAIN,
  };
}

function createYardRoom(capabilities: RoomCapabilities): RoomModule {
  const room = createLoadingYard();
  capabilities.scene.add(room.group);
  const colliders: ColliderHandle[] = loadingYardSolids().map((solid) => capabilities.colliders.add(solid));
  let disposed = false;
  return {
    interactables: [],
    fixedStep(): void { /* static: beat 6's director owns everything that moves */ },
    renderStep(): void { /* static */ },
    framing: () => null,
    diagnostics: () => ({ kind: 'none' }),
    dispose(): void {
      if (disposed) return;
      disposed = true;
      for (const handle of colliders) capabilities.colliders.remove(handle);
      capabilities.scene.remove(room.group);
    },
  };
}

export const YARD_ROOM_MODULE: RoomModuleDefinition = {
  room: 'yard',
  environment: yardEnvironment,
  create: (_options, capabilities) => createYardRoom(capabilities),
};
